import { useState, useEffect, useRef, memo, useMemo, useCallback, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import type { PluggableList } from "unified";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import { common } from "lowlight";
import powershell from "highlight.js/lib/languages/powershell";
import "highlight.js/styles/github-dark.css";
import { Copy, Check, ExternalLink, ChevronDown, ChevronUp, Code2, ExternalLink as NewTab } from "lucide-react";

// ── Chart.js (tree-shaken registration) ────────────────────────────
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Title,
} from "chart.js";
import { Pie, Bar, Doughnut, Line } from "react-chartjs-2";

ChartJS.register(
  ArcElement,
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Title,
);

// ── Chart type set ─────────────────────────────────────────────────
const CHART_LANGUAGES = new Set(["chart-pie", "chart-bar", "chart-doughnut", "chart-line"]);

// ── Inline Chart component ─────────────────────────────────────────
function InlineChart({ language, json }: { language: string; json: string }) {
  try {
    const parsed = JSON.parse(json);
    const { labels, datasets, options: userOptions } = parsed;
    const data = { labels, datasets };
    const options = {
      responsive: true,
      maintainAspectRatio: true,
      ...(userOptions ?? {}),
    };

    const chartEl = (() => {
      switch (language) {
        case "chart-pie": return <Pie data={data} options={options} />;
        case "chart-bar": return <Bar data={data} options={options} />;
        case "chart-doughnut": return <Doughnut data={data} options={options} />;
        case "chart-line": return <Line data={data} options={options} />;
        default: return null;
      }
    })();

    if (!chartEl) return null;

    return (
      <div className="my-3 rounded-lg border border-border bg-background p-4" style={{ maxHeight: 400 }}>
        {chartEl}
      </div>
    );
  } catch {
    return <CodeBlock className={`language-${language}`}>{json}</CodeBlock>;
  }
}

// ── Mermaid diagram component ──────────────────────────────────────
// Valid mermaid diagram types — used to pre-validate before attempting render
const MERMAID_KEYWORDS = /^\s*(graph|flowchart|sequenceDiagram|classDiagram|stateDiagram|erDiagram|gantt|pie|gitGraph|journey|mindmap|timeline|quadrantChart|sankey|xychart|block)/;

function MermaidDiagram({ code }: { code: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [svg, setSvg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    // Quick pre-validation: only attempt render if the code starts with a known diagram keyword
    if (!MERMAID_KEYWORDS.test(code)) {
      setError("Not a valid mermaid diagram");
      return;
    }

    (async () => {
      try {
        const mermaid = (await import("mermaid")).default;
        mermaid.initialize({ startOnLoad: false, theme: "dark", securityLevel: "strict" });
        const id = `mermaid-${Math.random().toString(36).slice(2, 10)}`;
        const { svg: rendered } = await mermaid.render(id, code);
        if (!cancelled) setSvg(rendered);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Mermaid render failed");
      } finally {
        // Clean up any error elements mermaid injects into the DOM
        if (!cancelled) {
          document.querySelectorAll("[id^=dmermaid-]").forEach((el) => el.remove());
          document.querySelectorAll(".mermaid-error").forEach((el) => el.remove());
        }
      }
    })();
    return () => { cancelled = true; };
  }, [code]);

  if (error) return <CodeBlock className="language-mermaid">{code}</CodeBlock>;
  if (!svg) return <div className="my-3 rounded-lg border border-border p-4 text-xs text-text-muted animate-pulse">Rendering diagram…</div>;

  return (
    <div
      ref={containerRef}
      className="my-3 rounded-lg border border-border bg-background p-4 overflow-x-auto [&>svg]:max-w-full [&>svg]:h-auto"
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

// ── Sandboxed HTML artifact ────────────────────────────────────────
function HtmlArtifact({ html }: { html: string }) {
  const [expanded, setExpanded] = useState(false);
  const [showSource, setShowSource] = useState(false);
  const iframeHeight = expanded ? 700 : 400;

  const openInNewTab = useCallback(() => {
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }, [html]);

  return (
    <div className="my-3 rounded-lg border border-border overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center justify-between bg-surface-hover px-3 py-1.5 text-[11px] text-text-muted border-b border-border">
        <span className="font-medium">Interactive Visualisation</span>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowSource(!showSource)} className="flex items-center gap-1 hover:text-text-primary transition-colors">
            <Code2 size={12} />
            <span>{showSource ? "Hide Source" : "View Source"}</span>
          </button>
          <button onClick={openInNewTab} className="flex items-center gap-1 hover:text-text-primary transition-colors">
            <NewTab size={12} />
            <span>Open in new tab</span>
          </button>
          <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-1 hover:text-text-primary transition-colors">
            {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            <span>{expanded ? "Collapse" : "Expand"}</span>
          </button>
        </div>
      </div>
      {/* Source view */}
      {showSource && (
        <pre className="overflow-x-auto bg-surface p-3 text-xs text-text-secondary border-b border-border max-h-[300px]">
          <code>{html}</code>
        </pre>
      )}
      {/* Sandboxed iframe */}
      <iframe
        srcDoc={html}
        sandbox="allow-scripts"
        className="w-full border-0 bg-white"
        style={{ height: iframeHeight }}
        title="HTML Visualisation"
      />
    </div>
  );
}

// ── Code block (original) ──────────────────────────────────────────
function CodeBlock({
  className,
  children,
}: {
  className?: string;
  children?: ReactNode;
}) {
  const [copied, setCopied] = useState(false);
  const match = /language-(\w+)/.exec(className || "");
  const language = match?.[1] || "";
  const code = String(children).replace(/\n$/, "");

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="group relative my-3 max-w-full overflow-hidden rounded-lg border border-border">
      <div className="flex items-center justify-between bg-surface-hover px-3 py-1.5 text-[11px] text-text-muted border-b border-border">
        <span className="font-mono">{language || "code"}</span>
        <button
          onClick={handleCopy}
          aria-label="Copy code block"
          title="Copy code block"
          className="flex items-center gap-1 text-text-muted hover:text-text-primary transition-colors"
        >
          {copied ? (
            <>
              <Check size={12} className="text-emerald-400" />
              <span>Copied</span>
            </>
          ) : (
            <>
              <Copy size={12} />
              <span>Copy</span>
            </>
          )}
        </button>
      </div>
      <pre className="!m-0 max-w-full overflow-x-auto whitespace-pre bg-[#0d1117] p-3 text-xs leading-relaxed">
        <code className={cnCodeClass(className)}>{children}</code>
      </pre>
    </div>
  );
}

function cnCodeClass(className?: string): string {
  return [
    className,
    "font-mono whitespace-pre text-xs leading-relaxed",
  ].filter(Boolean).join(" ");
}

function InlineCode({ children }: { children?: ReactNode }) {
  return (
    <code className="bg-surface-hover text-accent px-1.5 py-0.5 rounded text-[0.85em] font-mono">
      {children}
    </code>
  );
}

/*
 * ── Rich code block formats supported by agents ────────────────────
 *
 * Agents can output these fenced code blocks for inline rendering:
 *
 * ```chart-pie / chart-bar / chart-doughnut / chart-line
 * { "labels": [...], "datasets": [{ "data": [...], ... }], "options": {...} }
 * ```
 *
 * ```html-artifact / html-render
 * <html>...</html>
 * ```
 *
 * ```mermaid
 * graph TD; A-->B; B-->C;
 * ```
 */

// Memoized to prevent re-parsing unchanged content during streaming
export const MarkdownRenderer = memo(function MarkdownRenderer({ content }: { content: string }) {
  // Memoize plugins arrays so they're referentially stable
  const remarkPlugins = useMemo(() => [remarkGfm], []);
  // PowerShell is not in lowlight common set, so PS scripts fell back to
  // untokenized plaintext. Register it (plus aliases) on top of common.
  const rehypePlugins = useMemo<PluggableList>(
    () => [[rehypeHighlight, { languages: { ...common, powershell }, aliases: { powershell: ["ps", "ps1", "pwsh"] } }]],
    [],
  );

  return (
    <ReactMarkdown
      remarkPlugins={remarkPlugins}
      rehypePlugins={rehypePlugins}
      components={{
        code({ className, children, ...props }) {
          const isBlock =
            className?.includes("language-") ||
            (typeof children === "string" && children.includes("\n"));

          if (isBlock) {
            const langMatch = /language-(\S+)/.exec(className || "");
            const lang = langMatch?.[1] || "";
            const code = String(children).replace(/\n$/, "");

            // Chart.js inline charts
            if (CHART_LANGUAGES.has(lang)) {
              return <InlineChart language={lang} json={code} />;
            }

            // Sandboxed HTML artifacts
            if (lang === "html-artifact" || lang === "html-render") {
              return <HtmlArtifact html={code} />;
            }

            // Mermaid diagrams
            if (lang === "mermaid") {
              return <MermaidDiagram code={code} />;
            }

            return <CodeBlock className={className}>{children}</CodeBlock>;
          }
          return <InlineCode>{children}</InlineCode>;
        },
        table({ children }) {
          return (
            <div className="overflow-x-auto my-3 rounded-lg border border-border">
              <table className="min-w-full lg:w-full text-sm">{children}</table>
            </div>
          );
        },
        thead({ children }) {
          return <thead className="bg-surface-hover text-text-secondary text-xs uppercase tracking-wide">{children}</thead>;
        },
        th({ children }) {
          return <th className="px-3 py-2 lg:px-3.5 lg:py-2.5 text-left text-[12px] font-medium uppercase tracking-wider border-b border-border">{children}</th>;
        },
        td({ children }) {
          return <td className="px-3 py-2 lg:px-3.5 lg:py-2.5 border-b border-border">{children}</td>;
        },
        blockquote({ children }) {
          return (
            <blockquote className="border-l-3 border-accent/40 bg-accent/5 pl-4 py-2 my-3 rounded-r-lg text-text-secondary">
              {children}
            </blockquote>
          );
        },
        a({ href, children }) {
          const isExternal = href?.startsWith("http");
          return (
            <a
              href={href}
              target={isExternal ? "_blank" : undefined}
              rel={isExternal ? "noopener noreferrer" : undefined}
              className="text-accent hover:text-accent-hover underline underline-offset-2 inline-flex items-center gap-0.5"
            >
              {children}
              {isExternal && <ExternalLink size={10} className="shrink-0" />}
            </a>
          );
        },
        hr() {
          return <hr className="my-4 border-border" />;
        },
        ul({ children }) {
          return <ul className="list-disc pl-5 my-2 space-y-1">{children}</ul>;
        },
        ol({ children }) {
          return <ol className="list-decimal pl-5 my-2 space-y-1">{children}</ol>;
        },
        li({ children }) {
          return <li className="text-text-primary">{children}</li>;
        },
        p({ children }) {
          return <p className="my-2 leading-relaxed lg:text-[14px] lg:leading-[1.65]">{children}</p>;
        },
        h1({ children }) {
          return <h1 className="text-lg font-bold mt-4 mb-2">{children}</h1>;
        },
        h2({ children }) {
          return <h2 className="text-base font-bold mt-4 mb-2">{children}</h2>;
        },
        h3({ children }) {
          return <h3 className="text-sm font-semibold mt-3 mb-1.5">{children}</h3>;
        },
        h4({ children }) {
          return <h4 className="text-sm font-medium mt-2 mb-1">{children}</h4>;
        },
      }}
    >
      {content}
    </ReactMarkdown>
  );
});
